import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KATEGORIER = [
  "Bygdegård",
  "Kyrka / Församling",
  "Förening",
  "Marknad / Loppis",
  "Musik / Konsert",
  "Idrott / Friluftsliv",
  "Barn / Familj",
  "Annat",
];

export default function SkickaEvenemang() {
  const [form, setForm] = useState({
    titel: "",
    beskrivning: "",
    datum: "",
    tid: "",
    plats: "",
    adress: "",
    kategori: "",
    arrangor: "",
    kontakt_epost: "",
    kontakt_telefon: "",
    samtycke: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [galleri, setGalleri] = useState([]); // Build 96.21 — multi: array of {url, media_typ}
  const [uploadingImage, setUploadingImage] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (galleri.length >= 3) return;
    // Build 96.21 — samma logik som SkickaAnslag: bild 10 MB, video 60 MB.
    const isVideo = (file.type || "").startsWith("video/")
      || /\.(mp4|mov|webm|m4v)$/i.test(file.name || "");
    const maxBytes = isVideo ? 60 * 1024 * 1024 : 10 * 1024 * 1024;
    const maxLabel = isVideo ? "60 MB" : "10 MB";
    if (file.size > maxBytes) {
      setErrors(prev => ({ ...prev, submit: `${isVideo ? "Videon" : "Filen"} är för stor (max ${maxLabel}).` }));
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      setGalleri(prev => [...prev, { url: res.data.url, media_typ: res.data.media_typ || (isVideo ? "video" : "foto") }]);
      setErrors(prev => ({ ...prev, submit: null }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : "Kunde inte ladda upp filen. Försök igen eller välj en annan fil.";
      setErrors(prev => ({ ...prev, submit: msg }));
    }
    setUploadingImage(false);
    if (e.target) e.target.value = "";
  };

  const removeImage = (idx) => setGalleri(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!form.titel.trim()) e.titel = "Du behöver ange en titel";
    if (!form.beskrivning.trim()) e.beskrivning = "Du behöver ange en beskrivning";
    if (!form.datum) e.datum = "Välj datum";
    else {
      const today = new Date().toISOString().slice(0, 10);
      if (form.datum < today) e.datum = "Datumet har redan passerat";
    }
    if (!form.plats.trim()) e.plats = "Ange var evenemanget äger rum";
    if (!form.samtycke) e.samtycke = "Du behöver godkänna villkoren för att skicka in";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setTimeout(() => {
        const banner = document.querySelector('[data-testid="form-error-banner"]');
        if (banner) banner.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/evenemang/submit`, {
        titel: form.titel,
        beskrivning: form.beskrivning,
        datum: form.datum,
        tid: form.tid || null,
        plats: form.plats,
        adress: form.adress || null,
        kategori: form.kategori || null,
        arrangor: form.arrangor || null,
        kontakt_epost: form.kontakt_epost || null,
        kontakt_telefon: form.kontakt_telefon || null,
        bild_url: galleri[0]?.url || null,  // bakåtkompatibel
        galleri: galleri.length > 0 ? galleri.map(g => g.url) : null,
        samtycke: form.samtycke,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setErrors({ submit: typeof msg === "string" ? msg : "Något gick fel. Försök igen." });
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="evenemang-submission-confirmation" className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center sm:text-left">
          <CheckCircle size={48} className="text-pine mb-4 mx-auto sm:mx-0" />
          <h1 className="font-heading text-3xl font-semibold text-ink tracking-tight mb-3">Tack för ditt evenemang-tips!</h1>
          <p className="text-sm text-bark mb-2">Vi har tagit emot ditt evenemang. Redaktionen granskar och publicerar så snart som möjligt.</p>
          <p className="text-sm text-bark mb-8">Så fort det är publicerat syns det i Veckans händelser och i kalendern.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/veckans-handelser" data-testid="link-tillbaka-veckans" className="inline-flex items-center justify-center bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors no-underline">Visa veckans händelser</Link>
            <Link to="/" data-testid="link-tillbaka-start" className="inline-flex items-center justify-center border border-pine text-pine hover:bg-bone-alt font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors no-underline">Tillbaka till startsidan</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="skicka-evenemang-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Skicka in</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3">Tipsa om evenemang</h1>
      <p className="text-sm text-bark mb-8 leading-relaxed">Har ni en marknad, konsert, byamöte eller annat på gång? Skicka gärna in det så tar redaktionen hand om resten. Inget publiceras utan granskning.</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
        {Object.keys(errors).filter(k => k !== "submit").length > 0 && (
          <div
            data-testid="form-error-banner"
            role="alert"
            aria-live="assertive"
            className="bg-falu/5 border border-falu/40 rounded-sm p-4 flex gap-3 items-start"
          >
            <span className="inline-flex shrink-0 w-6 h-6 rounded-full bg-falu text-parchment items-center justify-center text-sm font-bold leading-none">!</span>
            <div className="flex-1">
              <p className="font-heading text-base font-semibold text-falu mb-1 leading-snug">
                Några fält saknas
              </p>
              <p className="text-sm text-bark/80 mb-2 leading-relaxed">
                Fyll i alla fält som är markerade med <span className="text-falu font-bold">*</span> innan du skickar in. De markerade fälten nedan är inte ifyllda:
              </p>
              <ul className="list-disc pl-5 text-sm text-bark space-y-0.5">
                {Object.entries(errors).filter(([k]) => k !== "submit").map(([k, msg]) => (
                  <li key={k}><strong>{msg}</strong></li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <FieldGroup label="Titel" help='T.ex. "Majmarknad i Rossön" eller "Konsert i Backe kyrka"' error={errors.titel} required>
          <input data-testid="field-titel" type="text" value={form.titel} onChange={e => set("titel", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Beskrivning" help="Beskriv kort vad som händer. 1–3 meningar räcker." error={errors.beskrivning} required>
          <textarea data-testid="field-beskrivning" value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={4} className="w-full px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none resize-y" />
        </FieldGroup>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldGroup label="Datum" error={errors.datum} required>
            <input data-testid="field-datum" type="date" value={form.datum} onChange={e => set("datum", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
          </FieldGroup>
          <FieldGroup label="Tid (frivilligt)" help="T.ex. 18:00">
            <input data-testid="field-tid" type="time" value={form.tid} onChange={e => set("tid", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
          </FieldGroup>
        </div>

        <FieldGroup label="Plats / lokal" help='T.ex. "Bygdegården Backe" eller "Rossöns IP"' error={errors.plats} required>
          <input data-testid="field-plats" type="text" value={form.plats} onChange={e => set("plats", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Adress (frivilligt)" help="Hjälper redaktionen att placera evenemanget på kartan.">
          <input data-testid="field-adress" type="text" value={form.adress} onChange={e => set("adress", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Kategori (frivilligt)">
          <select data-testid="field-kategori" value={form.kategori} onChange={e => set("kategori", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none">
            <option value="">Välj...</option>
            {KATEGORIER.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </FieldGroup>

        <FieldGroup label="Arrangör (frivilligt)" help="Förening, företag eller privatperson som står bakom evenemanget.">
          <input data-testid="field-arrangor" type="text" value={form.arrangor} onChange={e => set("arrangor", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label={`Bilder och video (${galleri.length} / 3, frivilligt)`} help="Upp till 3 filer. Bilder max 10 MB (JPG, PNG, WebP, GIF, HEIC). Video max 60 MB (MP4, MOV, WebM).">
          {galleri.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="galleri-thumbs">
              {galleri.map((item, i) => {
                const isVideo = item.media_typ === "video" || /\.(mp4|mov|webm|m4v)$/i.test(item.url || "");
                return (
                  <div key={item.url + i} className="relative inline-block">
                    {isVideo ? (
                      <video
                        src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                        muted
                        playsInline
                        className="w-32 h-32 object-cover rounded-sm border border-warm-border bg-black"
                      />
                    ) : (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`} alt={`Uppladdad ${i + 1}`} className="w-32 h-32 object-cover rounded-sm border border-warm-border" />
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`remove-galleri-${i}`}
                      className="absolute -top-2 -right-2 bg-falu hover:bg-falu-hover text-white rounded-full w-6 h-6 text-xs leading-none"
                      title="Ta bort"
                    >×</button>
                  </div>
                );
              })}
            </div>
          )}
          {galleri.length < 3 && (
            <input
              data-testid="field-bild"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
              onChange={handleAddImage}
              disabled={uploadingImage}
              className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white"
            />
          )}
          {uploadingImage && (
            <p data-testid="upload-progress" className="text-sm text-bark/70 mt-1 italic">Laddar upp…</p>
          )}
          {galleri.length >= 3 && (
            <p className="text-sm text-bark/70 italic">Max 3 filer uppnådda. Ta bort en för att lägga till en annan.</p>
          )}
        </FieldGroup>

        <FieldGroup label="Kontaktväg (frivilligt)" help="Om redaktionen behöver komplettera något.">
          <input data-testid="field-epost" type="email" placeholder="E-post" value={form.kontakt_epost} onChange={e => set("kontakt_epost", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none mb-3" />
          <input data-testid="field-telefon" type="tel" placeholder="Telefon" value={form.kontakt_telefon} onChange={e => set("kontakt_telefon", e.target.value)} className="w-full min-h-[56px] px-4 py-3 border border-warm-border rounded-sm text-lg bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <div className="flex items-start gap-3 mt-2">
          <input data-testid="field-samtycke" type="checkbox" checked={form.samtycke} onChange={e => set("samtycke", e.target.checked)} className="mt-1.5 w-5 h-5 accent-pine rounded" />
          <label className="text-base text-ink leading-relaxed">
            {/* Etappe 4.8 — uppdaterad bekräftelse-text med länkar till integritet + villkor-inskickat */}
            Jag bekräftar att jag har rätt att skicka in materialet och att vidalv.live får granska, redigera och publicera det enligt{" "}
            <Link to="/villkor-inskickat" target="_blank" className="text-falu underline">villkoren för inskickat material</Link> och{" "}
            <Link to="/integritet" target="_blank" className="text-falu underline">integritetspolicyn</Link>.
          </label>
        </div>
        {errors.samtycke && <p className="text-falu text-sm -mt-4">{errors.samtycke}</p>}
        {/* Etappe 4.8 — dataskyddshinweis */}
        <p data-testid="dataskydd-hint" className="text-xs text-bark/60 leading-relaxed -mt-2">
          Vi använder dina uppgifter för att kunna granska tipset, publicera relevant information
          och kontakta dig vid frågor. Läs mer i vår{" "}
          <Link to="/integritet" className="text-falu hover:text-falu-hover">integritetspolicy</Link>.
        </p>
        {errors.submit && <p className="text-falu text-base">{errors.submit}</p>}

        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-evenemang-button"
          className="w-full sm:w-auto bg-falu hover:bg-falu-hover disabled:opacity-50 text-parchment font-body font-semibold text-sm px-6 py-3.5 rounded-sm transition-colors mt-2"
        >
          {submitting ? "Skickar..." : "Skicka in evenemang"}
        </button>
      </form>
    </div>
  );
}

function FieldGroup({ label, help, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-ink mb-1">
        {label}{required && <span className="text-falu ml-1">*</span>}
      </label>
      {help && <p className="text-xs text-bark mb-2">{help}</p>}
      {children}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
